import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import AppLink from '@/components/elementary/AppLink'
import { Tag } from '@/components/elementary/Tag'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import styles from './NavbarMenuLink.module.css'

interface NavbarMenuLinkProps {
	readonly link: Data.Component<'layout-utilities.navbar-links'>
	readonly isDropDownItem?: boolean
	readonly testId?: string
	onClick?: () => void
}

export function NavbarMenuLink({
	link,
	isDropDownItem = false,
	testId,
	onClick,
}: NavbarMenuLinkProps) {
	const t = useTranslations('navbar')
	return (
		<AppLink
			variant="primaryLink"
			size="none"
			href={link.href ?? ''}
			key={link.id}
			openExternalInNewTab={!!link.newTab}
			className="flex flex-col gap-2 w-fit items-start"
			data-test-id={testId}
			onClick={onClick}
		>
			<div className={cn('flex gap-2 w-fit items-center', link?.description && 'items-start')}>
				{link.icon && (
					<div
						className={cn(
							'flex justify-center shrink-0 size-8 items-center p-1 border border-neutral-light-400 rounded',
							link.IsIconPink ? 'bg-peach-200' : 'bg-transparent',
						)}
					>
						<BasicImage component={link?.icon} className="object-contain size-4" />
					</div>
				)}

				<div className="flex flex-col">
					{link?.title && (
						<Paragraph
							fontSize="bodyBoldMedium"
							className={cn(
								'text-inherit',
								styles['nav-content-menu-link'],
								!link.description && !link?.subTitle ? 'lg:max-w-32' : '',
							)}
						>
							{link.title}
						</Paragraph>
					)}

					{link?.description && !isDropDownItem && (
						<Paragraph
							className={cn('text-inherit whitespace-nowrap', styles['nav-content-menu-link'])}
						>
							{link.description}
						</Paragraph>
					)}

					{link?.subTitle && !isDropDownItem && (
						<div className="flex gap-2 items-center">
							<Paragraph
								className={cn('text-inherit whitespace-nowrap', styles['nav-content-menu-link'])}
							>
								{link.subTitle}
							</Paragraph>

							{link?.hasNewBadge && (
								<Tag name={t('newBadge')} variant="green-success" size="small" />
							)}
						</div>
					)}
				</div>
			</div>

			{link?.description && isDropDownItem && (
				<Paragraph fontSize="bodyRegularMedium">{link.description}</Paragraph>
			)}
		</AppLink>
	)
}
